/**
 * Clipboard writes routed through the Electron clipboard bridge.
 *
 * `navigator.clipboard.writeText` rejects with NotAllowedError
 * "Document is not focused" whenever the window loses focus before an async
 * copy lands (Chromium focus requirement) — the copy silently fails, and at
 * un-awaited call sites the rejection used to surface as a renderer crash
 * report. The preload bridge (`window.electronAPI.clipboard`) wraps
 * Electron's native clipboard, which has no focus requirement and never
 * rejects, so it is always preferred; the `navigator.clipboard` path remains
 * only as a fallback for contexts without the bridge (unit tests) or when the
 * bridge reports the write did not land.
 *
 * Every copy in the renderer should go through these helpers — never call
 * `navigator.clipboard` directly.
 */

/**
 * Write plain text to the clipboard. Never rejects.
 * Resolves to whether the write succeeded (the bridge path always does).
 */
export async function writeClipboardText(text: string): Promise<boolean> {
  const bridge = typeof window !== 'undefined' ? window.electronAPI?.clipboard : undefined;
  if (bridge?.writeText) {
    // Awaited: the bridge forwards to the main process, so the write has not
    // landed when the call returns. Callers that read the clipboard back (and
    // the e2e specs that assert on it) depend on this resolving first.
    if (await bridge.writeText(text)) return true;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Write a rich (text/html + text/plain) payload to the clipboard so
 * Teams/Outlook/Word/Confluence paste a formatted table while Notepad,
 * terminals and Monaco get the plain form. Never rejects; falls back to a
 * plain-text write when no rich path is available.
 */
export async function writeClipboardRich(text: string, html: string): Promise<boolean> {
  const bridge = typeof window !== 'undefined' ? window.electronAPI?.clipboard : undefined;
  if (bridge?.writeRich) {
    if (await bridge.writeRich(text, html)) return true;
  }
  const ClipboardItemCtor = (globalThis as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
  if (typeof ClipboardItemCtor !== 'undefined') {
    try {
      const item = new ClipboardItemCtor({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      // Fall through to the plain-text fallback below.
    }
  }
  return writeClipboardText(text);
}
